import React, { Component } from 'react';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import classNames from 'classnames';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  arrayMove
} from 'react-sortable-hoc';
import {
  cloneDeep,
  includes,
  find,
  isArray,
  toArray,
  isEmpty,
  get,
  pull,
  set,
  isUndefined,
  isFunction,
  keys,
  forEach,
  defer,
  reject as rejectItem,
  values,
  without,
  flatten
} from 'lodash';

import AppBar from '@material-ui/core/AppBar';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

import MoreVertIcon from '@material-ui/icons/MoreVert';
import DragHandleIcon from '@material-ui/icons/DragIndicator';
import AddIcon from '@material-ui/icons/Add';

import { withStyles } from '@material-ui/core/styles';

import {
  formFields,
  groupFields,
  schemaFields,
  formFieldsConstraints,
  schemaFieldsConstraints
} from './fieldTypes';

import FieldsAndFieldGroups from './FieldsAndFieldGroups';
import FieldDetailsForm from './FieldDetailsForm';
import SectionForm from './SectionForm';
import Stack from '../../ui/Stack';

const DragHandle = SortableHandle(() => <DragHandleIcon />);

const SortableItem = SortableElement(({ children, className }) => (
  <div className={className}>{children}</div>
));

const SortableList = SortableContainer(({ context }) => {
  const { classes } = context.props;
  const open = Boolean(context.state.anchorEl);

  const items = context.state.formData.fields.map(
    ({ type, name, title, key, id }, index) => {
      const fieldType = find(flatten(formFields), { type });
      return (
        <SortableItem
          key={`item-${id}`}
          index={index}
          className={classes.sortableItem}
        >
          <div
            className={classes.formFieldContainer}
            key={key}
            onClick={() => {
              context.onEditField(key);
            }}
          >
            <Button
              onClick={e => context.addField(e, index)}
              size="small"
              fullWidth
              className="addButtons top"
            >
              <AddIcon />
            </Button>
            <Paper className={classes.formField}>
              <Grid container spacing={16} er>
                <Grid item>
                  <span
                    onClick={e => e.stopPropagation()}
                    className={classes.dragHandle}
                  >
                    <DragHandle />
                  </span>
                  <span className={classes.formFieldIcon}>
                    <Chip
                      avatar={
                        <Avatar className={classes.chipAvatar}>
                          {fieldType.icon()}
                        </Avatar>
                      }
                      label={fieldType.name}
                    />
                  </span>
                </Grid>
                <Grid item xs={12} sm container>
                  <Grid item xs container direction="column" spacing={16}>
                    <Grid item xs>
                      <span className={classes.formFieldName}>{title}</span>
                      <span className={classes.formFieldKey}>#{key}</span>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <IconButton
                      aria-label="More"
                      aria-owns={open ? 'long-menu' : null}
                      aria-haspopup="true"
                      onClick={e => context.handleClick(e, key)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
            <Button
              size="small"
              fullWidth
              className="addButtons bottom"
              onClick={e => context.addField(e, index + 1)}
            >
              <AddIcon />
            </Button>
          </div>
        </SortableItem>
      );
    }
  );

  return <div>{items}</div>;
});

const styles = theme => ({
  fieldsContainer: {
    padding: theme.spacing(4)
  },

  sectionTab: {
    minWidth: 72
  },

  chipAvatar: {
    background: theme.palette.primary.main,
    color: '#fff'
  },

  formFields: {
    padding: 0,
    margin: 0,
    fontSize: 16
  },

  sortableItem: {
    zIndex: 4,
    position: 'relative'
  },

  formFieldContainer: {
    zIndex: 5,
    position: 'relative',
    transition: 'padding 300ms ease-in-out',
    transitionDelay: '300ms',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    '& .addButtons': {
      padding: 0,
      minHeight: 22,
      transition: 'opacity, top, bottom, 300ms ease-in-out',
      transitionDelay: '300ms',
      border: `1px dashed ${theme.palette.grey['400']}`,
      zIndex: 1,
      position: 'absolute',
      width: '100%',
      opacity: 0,
      '&.top': {
        top: 0
      },
      '&.bottom': {
        bottom: 0
      },
      '& svg': {
        fontSize: 16
      }
    },
    '&:not( :hover )': {
      transitionDelay: '100ms'
    },
    '&:hover': {
      // paddingTop: theme.spacing(2),
      // paddingBottom: theme.spacing(2),
      '& .addButtons': {
        opacity: 1,
        '&.top': {
          top: -11
        },
        '&.bottom': {
          bottom: -11
        }
      }
    }
  },

  formField: {
    position: 'relative',
    zIndex: 5,
    padding: theme.spacing(),
    // marginBottom: theme.spacing(2),
    // marginTop: theme.spacing(2),
    cursor: 'pointer'
  },

  formFieldIcon: {
    marginRight: theme.spacing(2)
  },

  formFieldName: {
    marginRight: theme.spacing(2),
    lineHeight: 2
  },

  formFieldKey: {
    color: theme.palette.grey['600'],
    fontSize: 14,
    lineHeight: 2
  },

  alignItemsCenter: {
    alignItems: 'center'
  },

  button: {
    border: `1px dashed ${theme.palette.grey['400']}`,
    marginTop: theme.spacing(2),
    width: '100%'
  },

  dragHandle: {
    height: 28,
    color: theme.palette.grey['400'],
    transition: 'color 300ms ease-in-out',
    marginRight: theme.spacing(),
    verticalAlign: 'middle',
    display: 'inline-block',
    fontSize: 24,
    cursor: 'move',
    cursor: 'grab',
    '&:hover': {
      color: theme.palette.primary.main
    },
    '&:active': {
      cursor: 'grabbing',
      color: theme.palette.primary.main
    }
  },

  addSectionBtn: {
    color: '#fff'
  },
  sectionsBar: {
    zIndex: 5,
    top: 108,
    opacity: 0.9
  }
});

class SchemaBuilder extends Component {
  state = {
    section: 0,
    insertIndex: 'none',
    showFieldStack: false,
    showEditFieldStack: false,
    showSectionStack: false,
    fieldPath: '',
    formData: {
      sections: [{ name: 'Main', key: 'main' }],
      fields: []
    },
    anchorEl: null
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    // const { fields } = this.state.formData;
    const state = cloneDeep(this.state);

    state.formData.fields = arrayMove(
      state.formData.fields,
      oldIndex,
      newIndex
    );
    this.setState(state);
    // this.setState({
    //   ...this.state,
    //   ...{
    //     formData: {
    //       ...this.state.formData,
    //       ...{ fields: arrayMove(fields, oldIndex, newIndex) }
    //     }
    //   }
    // });
  };

  handleClick = (e, key) => {
    e.stopPropagation();
    this.setState({ anchorEl: e.currentTarget, fieldPath: key });
  };

  handleClose = e => {
    e.stopPropagation();
    this.setState({ anchorEl: null });
  };

  handleChange = (event, value) => {
    this.setState({ value });
  };

  toggleFieldStack = () => {
    this.setState(state => ({ showFieldStack: !state.showFieldStack }));
  };

  toggleSectionStack = e => {
    this.setState(state => ({ showSectionStack: !state.showSectionStack }));
  };

  toggleEditFieldStack = () => {
    this.setState(state => ({ showEditFieldStack: !state.showEditFieldStack }));
  };

  onSectionChange = (e, index) => {
    if (index < this.state.formData.sections.length) {
      this.setState({ section: index });
    }
  };

  onAddSection = section => {
    const state = cloneDeep(this.state);
    state.formData.sections.push(section);
    state.showSectionStack = false;
    this.setState(state);
  };

  onRemoveField = ({ id, key }) => {
    const state = cloneDeep(this.state);
    if (this.state.isEditingSubFields) {
      const field = this.getFieldData({ state, id: state.editing.id });
      if (id) {
        set(field, 'options', rejectItem(field.options, { id }));
      } else {
        set(field, 'options', rejectItem(field.options, { key }));
      }
      state.editingSubFields = null;
    } else {
      if (id) {
        state.formData.fields = rejectItem(state.formData.fields, { id });
      } else {
        state.formData.fields = rejectItem(state.formData.fields, { key });
      }
      state.editing = null;
    }
    state.showEditFieldStack = false;
    state.anchorEl = null;
    this.setState(state);
  };

  addField = (e, index) => {
    e.stopPropagation();
    this.setState({ insertIndex: index });
    this.toggleFieldStack();
  };

  onAddField = ({ field: data }) => {
    const dropData = data.split(', ');
    const type = dropData[0];
    const dropType = dropData[1] || 'field';
    const state = cloneDeep(this.state);

    // Ensure state.formData.fields is always an array
    // if (!isArray(get(state, 'formData.fields'))) {
    //   set(state, 'formData.fields', []);
    // }

    if (dropType === 'field') {
      const formField = { type, id: Date.now() };
      const field = find(flatten(formFields), { type });
      field
        .fields({ props: cloneDeep(this.props), state })
        .forEach(({ key, defaultValue }) =>
          set(
            formField,
            key,
            isFunction(defaultValue) ? defaultValue() : defaultValue
          )
        );

      if (includes(['radio', 'select', 'repeater', 'fieldset'], type)) {
        set(formField, 'options', []);
      }

      if (state.isEditingSubFields) {
        const currentField = this.getFieldData({ state, id: state.editing.id });
        if (isEmpty(currentField.options) || !isArray(currentField.options)) {
          set(currentField, 'options', []);
        }
        currentField.options.push(formField);
      } else {
        if (state.insertIndex !== 'none') {
          state.formData.fields.splice(state.insertIndex, 0, formField);
        } else {
          state.formData.fields.push(formField);
        }
      }
      state.insertIndex = 'none';
      state.showFieldStack = false;
      state.showEditFieldStack = true;
      state.fieldPath = formField.key;
      // this.setState(state, () => this.validateFieldData(formField));
      this.setState(state);
    } else {
      const field = find(flatten(groupFields), { type });

      if (field.groupType) {
        const formField = { type: field.groupType, id: Date.now() };
        field
          .fields({ props: cloneDeep(this.props), state })
          .forEach(({ key, defaultValue }) =>
            set(
              formField,
              key,
              isFunction(defaultValue) ? defaultValue() : defaultValue
            )
          );

        if (
          includes(['radio', 'select', 'repeater', 'fieldset'], field.groupType)
        ) {
          set(formField, 'options', []);
        }
        state.formData.fields.push(formField);
      }
      field.formFields.forEach((fieldToPush, index) => {
        fieldToPush.id = Date.now() + index;

        if (!state.isEditingSubFields) {
          if (includes(['fieldset', 'repeater'], field.groupType)) {
            const options = get(
              state.formData.fields,
              `[${state.formData.fields.length - 1}].options`,
              []
            );
            if (!isArray(options)) {
              set(
                state.formData.fields,
                `[${state.formData.fields.length - 1}].options`,
                []
              );
            }
            state.formData.fields[
              state.formData.fields.length - 1
            ].options.push(fieldToPush);
          } else {
            state.formData.fields.push(fieldToPush);
          }
        } else {
          const currentField = this.getFieldData({
            state,
            id: state.editing.id
          });
          if (isEmpty(currentField.options) || !isArray(currentField.options)) {
            set(currentField, 'options', []);
          }
          currentField.options.push(fieldToPush);
        }
      });
      state.insertIndex = 'none';
      state.showFieldStack = false;
      this.setState(state);
    }
  };

  onEditField = key => {
    this.setState({ showEditFieldStack: true, fieldPath: key, anchorEl: null });
  };

  getContainer = () => {
    return this.container;
  };

  render() {
    const { classes, theme } = this.props;
    const {
      showFieldStack,
      showEditFieldStack,
      showSectionStack,
      formData,
      fieldPath,
      anchorEl,
      section
    } = this.state;

    const open = Boolean(anchorEl);

    return (
      <Grid container justify="center">
        <AppBar className={classes.sectionsBar} position="sticky">
          <Tabs value={section} onChange={this.onSectionChange} scrollable>
            {formData.sections.map(section => (
              <Tab
                classes={{ root: classes.sectionTab }}
                label={section.name}
              />
            ))}
            <Tab
              onClick={this.toggleSectionStack}
              classes={{ root: classes.sectionTab }}
              icon={
                <Button size="small" className={classes.addSectionBtn}>
                  <AddIcon /> Add Section
                </Button>
              }
            />
          </Tabs>
        </AppBar>
        <Grid item xs={12} md={10}>
          <div className={classes.fieldsContainer}>
            {/* {JSON.stringify(this.state)} */}
            <div className={classes.formFields}>
              <SortableList
                ref={this.container}
                context={this}
                items={this.state.formData.fields}
                onSortEnd={this.onSortEnd}
                distance={10}
              />
            </div>
            <Button
              size="large"
              color="primary"
              className={classes.button}
              onClick={this.toggleFieldStack}
            >
              Add Field
            </Button>
          </div>
        </Grid>
        {/* </div> */}

        <Menu
          id="long-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={this.handleClose}
        >
          <MenuItem
            onClick={e => {
              e.stopPropagation();
              this.onRemoveField({ key: this.state.fieldPath });
            }}
          >
            Remove Field
          </MenuItem>
          <MenuItem
            onClick={e => {
              e.stopPropagation();
              this.onEditField(this.state.fieldPath);
            }}
          >
            Edit Field
          </MenuItem>
          <MenuItem
            onClick={e => {
              e.stopPropagation();
            }}
          >
            Add Field Above
          </MenuItem>
          <MenuItem
            onClick={e => {
              e.stopPropagation();
            }}
          >
            Add Field Below
          </MenuItem>
          <MenuItem
            onClick={e => {
              e.stopPropagation();
            }}
          >
            Move to Section
          </MenuItem>
          ))}
        </Menu>

        <Stack
          title="Add Fields"
          size="large"
          onClose={this.toggleFieldStack}
          show={showFieldStack}
        >
          <FieldsAndFieldGroups onAdd={this.onAddField} />
        </Stack>

        <Stack
          title="Edit Field"
          size="large"
          onClose={this.toggleEditFieldStack}
          show={showEditFieldStack}
        >
          <FieldDetailsForm
            fieldData={find(formData.fields, { key: fieldPath })}
            onRemoveField={this.onRemoveField}
            onChange={this.handleChange}
          />
        </Stack>

        <Stack
          direction="left"
          title="Sections"
          size="small"
          onClose={this.toggleSectionStack}
          show={showSectionStack}
        >
          <SectionForm onSave={this.onAddSection} />
        </Stack>
      </Grid>
    );
  }
}

export default connect(
  state => ({}),
  {}
)(hot(module)(withStyles(styles)(SchemaBuilder)));
